/**
 * The assets browser's left side: the files (`FileTree`: the project's, this computer's), then
 * every source of presets (built-in, this computer, yours, your team's), each with its folders
 * as a tree. Click to browse; right-click for New / Rename / Delete; drop a
 * preset card on a folder to move it there (same source) or copy it (another source).
 */
import { For, Show, createSignal, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import type { PresetSource, SourceId } from "../../state/presets";
import { childFolders, countWithin, folderName } from "../../shared/presetTree";
import type { Folders } from "./folders";
import type { FileTools } from "./fileTools";
import { FileTree } from "./FileTree";

/** The tree of every source: files first, then presets. */
export function FolderTree(props: { folders: Folders; files: FileTools }): JSX.Element {
  const { presets } = useApp();
  return (
    <nav class="ptree" aria-label="Assets">
      <span class="ptree-heading">Files</span>
      <FileTree tools={props.files} />
      <span class="ptree-heading">Presets</span>
      <For each={presets.sources()}>{(source) => <SourceRows source={source} folders={props.folders} />}</For>
    </nav>
  );
}

/** One source: its row, then its folders. */
function SourceRows(props: { source: PresetSource; folders: Folders }): JSX.Element {
  const { presets, assets, menu } = useApp();
  const here = () => !assets.place() && presets.place().source === props.source.id && presets.place().folder === "";
  return (
    <div class="ptree-source" data-source={props.source.id}>
      <button
        type="button"
        class="ptree-row ptree-top"
        aria-current={here() ? "true" : undefined}
        title={props.source.readOnly ? props.source.why : undefined}
        data-tree-source={props.source.id}
        data-tree-folder=""
        data-drop-source={props.source.readOnly ? undefined : props.source.id}
        data-drop-folder=""
        onClick={() => (assets.go(null), presets.go(props.source.id, ""))}
        onContextMenu={(e) => menu.open(e, props.folders.menu(props.source.id, ""))}
      >
        <span class="ptree-name">{props.source.label}</span>
        <Show when={props.source.readOnly}>
          <span class="ptag" data-read-only>
            read-only
          </span>
        </Show>
        <span class="ptree-count">{props.source.library.presets.length}</span>
      </button>
      <FolderRows source={props.source} parent="" depth={1} folders={props.folders} />
    </div>
  );
}

/** The folders inside `parent`, each followed by its own, plus the name being typed there. */
function FolderRows(props: { source: PresetSource; parent: string; depth: number; folders: Folders }): JSX.Element {
  const { presets, assets, menu } = useApp();
  const [closed, setClosed] = createSignal<ReadonlySet<string>>(new Set());
  const id = (): SourceId => props.source.id;
  const typingHere = () => {
    const n = props.folders.naming();
    return n && n.source === id() && n.parent === props.parent && n.renaming === null;
  };
  return (
    <>
      <For each={childFolders(props.source.library, props.parent)}>
        {(path) => {
          const renaming = () => {
            const n = props.folders.naming();
            return !!n && n.source === id() && n.renaming === path;
          };
          const open = () => !closed().has(path);
          const hasChildren = () => childFolders(props.source.library, path).length > 0;
          return (
            <>
              <Show when={!renaming()} fallback={<NameInput depth={props.depth} value={folderName(path)} folders={props.folders} />}>
                <button
                  type="button"
                  class="ptree-row"
                  style={{ "padding-left": `${8 + props.depth * 14}px` }}
                  aria-current={!assets.place() && presets.place().source === id() && presets.place().folder === path ? "true" : undefined}
                  data-tree-source={id()}
                  data-tree-folder={path}
                  data-drop-source={props.source.readOnly ? undefined : id()}
                  data-drop-folder={path}
                  onClick={() => (assets.go(null), presets.go(id(), path))}
                  onDblClick={() => props.folders.startRename(id(), path)}
                  onContextMenu={(e) => menu.open(e, props.folders.menu(id(), path))}
                >
                  <span
                    class="ptree-arrow"
                    classList={{ open: open(), none: !hasChildren() }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const next = new Set(closed());
                      if (open()) next.add(path);
                      else next.delete(path);
                      setClosed(next);
                    }}
                  >
                    ▸
                  </span>
                  <span class="ptree-name">{folderName(path)}</span>
                  <span class="ptree-count">{countWithin(props.source.library, path)}</span>
                </button>
              </Show>
              <Show when={open()}>
                <FolderRows source={props.source} parent={path} depth={props.depth + 1} folders={props.folders} />
              </Show>
            </>
          );
        }}
      </For>
      <Show when={typingHere()}>
        <NameInput depth={props.depth} value="" folders={props.folders} />
      </Show>
    </>
  );
}

/** The field where a folder's name is typed: Enter keeps it, Escape (or leaving it empty) drops it. */
function NameInput(props: { depth: number; value: string; folders: Folders }): JSX.Element {
  let done = false;
  /** Ends typing once (Enter, then the blur that follows, must not finish twice). */
  const end = (name: string | null) => {
    if (done) return;
    done = true;
    if (name === null) props.folders.cancel();
    else void props.folders.finish(name);
  };
  return (
    <input
      class="field ptree-input"
      id="preset-folder-name"
      aria-label="Folder name"
      placeholder="Folder name"
      maxlength="60"
      style={{ "margin-left": `${8 + props.depth * 14}px` }}
      value={props.value}
      ref={(el) => queueMicrotask(() => (el.focus(), el.select()))}
      onKeyDown={(e) => {
        if (e.key === "Enter") end(e.currentTarget.value);
        if (e.key === "Escape") end(null);
        e.stopPropagation();
      }}
      onBlur={(e) => end(e.currentTarget.value)}
    />
  );
}
